import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Message } from '@/components/icons';

interface SectionCommentBubbleProps {
    count: number;
    onOpenAllComments?: () => void;
}

function getPageRight(anchor: HTMLElement): number | null {
    let el: HTMLElement | null = anchor.parentElement;
    while (el) {
        if (el.id?.startsWith('report-page-')) {
            return el.getBoundingClientRect().right;
        }
        el = el.parentElement;
    }
    return null;
}

function SectionCommentBubbleComponent({ count, onOpenAllComments }: SectionCommentBubbleProps) {
    const anchorRef = useRef<HTMLSpanElement>(null);
    const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
    const animationFrameRef = useRef<number | null>(null);

    const syncPosition = useCallback(() => {
        if (!anchorRef.current) return;

        const anchorRect = anchorRef.current.getBoundingClientRect();
        const pageRight = getPageRight(anchorRef.current);
        if (pageRight === null) return;

        const scrollContainer = document.querySelector('[data-preview-container]');
        if (scrollContainer) {
            const containerRect = scrollContainer.getBoundingClientRect();
            if (anchorRect.top < containerRect.top || anchorRect.top > containerRect.bottom) {
                setPos((prev) => (prev === null ? prev : null));
                return;
            }
        }

        setPos((prev) => {
            const nextPos = { top: anchorRect.top, left: pageRight + 10 };

            if (
                prev &&
                Math.abs(prev.top - nextPos.top) < 0.5 &&
                Math.abs(prev.left - nextPos.left) < 0.5
            ) {
                return prev;
            }

            return nextPos;
        });
    }, []);

    const scheduleSyncPosition = useCallback(() => {
        if (animationFrameRef.current !== null) return;

        animationFrameRef.current = window.requestAnimationFrame(() => {
            animationFrameRef.current = null;
            syncPosition();
        });
    }, [syncPosition]);

    useEffect(() => {
        if (count === 0) return;
        scheduleSyncPosition();

        const scrollContainer = document.querySelector('[data-preview-container]');
        scrollContainer?.addEventListener('scroll', scheduleSyncPosition, {
            passive: true,
        });
        window.addEventListener('resize', scheduleSyncPosition);

        const zoomEl = document.querySelector('[data-report-zoom]');
        const observer = new MutationObserver(scheduleSyncPosition);
        if (zoomEl) {
            observer.observe(zoomEl, { attributes: true, attributeFilter: ['data-report-zoom'] });
        }

        return () => {
            scrollContainer?.removeEventListener('scroll', scheduleSyncPosition);
            window.removeEventListener('resize', scheduleSyncPosition);
            observer.disconnect();
            if (animationFrameRef.current !== null) {
                window.cancelAnimationFrame(animationFrameRef.current);
                animationFrameRef.current = null;
            }
        };
    }, [count, scheduleSyncPosition]);

    if (count === 0) return null;

    return (
        <>
            <span ref={anchorRef} className="absolute right-0 top-0 size-0" aria-hidden />
            {pos &&
                createPortal(
                    <div
                        style={{
                            position: 'fixed',
                            top: pos.top + 4,
                            left: pos.left,
                            zIndex: 10,
                        }}
                        className="flex cursor-pointer select-none items-center gap-1 rounded-full border border-brand/20 bg-brand-subtle px-2 py-0.5 text-xs font-semibold text-brand opacity-70 shadow-sm transition-opacity hover:opacity-100"
                        data-testid="section-comment-bubble"
                        onClick={onOpenAllComments}
                    >
                        <Message size={13} />
                        <span>{count}</span>
                    </div>,
                    document.body,
                )}
        </>
    );
}

export const SectionCommentBubble = memo(SectionCommentBubbleComponent);
